import type { PropsWithChildren } from "react";
import { cloneElement, isValidElement, useId } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";

type TooltipProps = {
  anchorOnChild?: boolean;
  className?: string;
  content: string;
  disabled?: boolean;
};

export const Tooltip = ({
  anchorOnChild,
  children,
  className = "inline-flex",
  content,
  disabled,
}: PropsWithChildren<TooltipProps>) => {
  /*
    The id must be stable across renders, or the
    anchor and the tooltip drifted apart and the tooltip stopped opening.
  */
  const id = useId();

  if (disabled) {
    return children;
  }

  const anchoredChild =
    anchorOnChild && isValidElement<{ "data-tooltip-id"?: string }>(children)
      ? cloneElement(children, { "data-tooltip-id": id })
      : null;

  return (
    <>
      {/* A span, not a button: the children are often interactive themselves. */}
      <span className={className} data-tooltip-id={anchoredChild === null ? id : undefined}>
        {anchoredChild === null ? children : anchoredChild}
      </span>

      <ReactTooltip
        className="!z-50 !max-w-xs !rounded-md !border !border-line-strong !bg-surface-active !text-xs !whitespace-normal !text-content !shadow-popover [--rt-opacity:1]"
        content={content}
        id={id}
        place="top"
        positionStrategy="fixed"
      />
    </>
  );
};
